import {
  useTranslate,
  useApiUrl,
  useHandleNotification,
  IResourceComponentsProps,
  CrudFilters,
} from "@pankod/refine-core";
import { LoadingOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import {
  Form,
  Button,
  CreateButton,
  List,
  Table,
  TextField,
  useTable,
  getDefaultSortOrder,
  DateField,
  Space,
  EditButton,
  DeleteButton,
  ShowButton,
  Upload,
  Input,
  Icons,
  Select,
  useSelect,
} from "@pankod/refine-antd";
import {IEvent, ILanguage, IQuiz} from "interfaces";
import { useState } from 'react';
import { HttpError } from "@pankod/refine-core";
import DeleteAllButton from "./deleteAllButton";
import DownloadButton from "./downloadButton";
import KeywordTagsSelect from "components/keywordTagsSelect";
import { useInvalidate } from "@pankod/refine-core";

export const QuizList: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const handleNotification = useHandleNotification();
  const invalidate = useInvalidate();

  const [loading, setLoading] = useState(false);
  const apiUrl = useApiUrl();
  const APIToken = localStorage.getItem("api-token");

  const handleChange: UploadProps['onChange'] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      setLoading(false);
      handleNotification({
        message: "Bulk upload has been successful",
        type: "success",
        key: "bulk-upload-error",
      });

      // Refresh page
      invalidate({
        resource: "quiz",
        invalidates: ["list"],
      });
    }
    if (info.file.status === 'error') {
      setLoading(false);
      console.log(info.file.response)
      handleNotification({
        message: "Error: " + info.file.response.message,
        type: "error",
        key: "bulk-upload-error",
      });
    }
  };

  const initialKeywords = (() => {
    const params = new URLSearchParams(window.location.search);
    return params.getAll("keywords").filter(Boolean);
  })();

  const { tableProps, sorter, searchFormProps } = useTable<
    IQuiz,
    HttpError,
    { q: string; lang: string; status: string; event_id: string; keywords: string[] }
  >({
    initialSorter: [
      {
        field: "quiz_id",
        order: "desc",
      },
    ],
    initialFilter:
      initialKeywords.length > 0
        ? [{ field: "keywords", operator: "eq", value: initialKeywords }]
        : undefined,
    onSearch: (params) => {
      const filters: CrudFilters = [];
      const { q, lang, status, event_id, keywords } = params;

      filters.push({ field: "q", operator: "eq", value: q });
      filters.push({ field: "lang", operator: "eq", value: lang });
      filters.push({ field: "status", operator: "eq", value: status });
      filters.push({ field: "event_id", operator: "eq", value: event_id });
      filters.push({
        field: "keywords",
        operator: "eq",
        value: keywords && keywords.length > 0 ? keywords : undefined,
      });

      return filters;
    },
  });

  const { selectProps: languageSelectProps } = useSelect<ILanguage>({
    resource: "language",
    optionLabel: "name",
    optionValue: "lang_code",
  });

  const { selectProps: eventSelectProps } = useSelect<IEvent>({
    resource: "event",
    optionLabel: "name",
    optionValue: "event_id",
  });

  return (
    (<List
      headerProps={{
        extra:[
          <Form
              {...searchFormProps}
              style={{ margin: 0 }}
              initialValues={{
                q: "",
                lang: "",
                status: "",
                event_id: "",
                keywords: initialKeywords,
              }}
              onFinish={searchFormProps.onFinish}
          >
            <Input.Group compact>
              <Form.Item name="q" noStyle>
                <Input
                    style={{ width: 180 }}
                    placeholder="Search"
                    allowClear
                    prefix={<Icons.SearchOutlined />}
                    onPressEnter={() => searchFormProps.form?.submit()}
                    onChange={() => {/* typing doesn't submit automatically */}}
                />
              </Form.Item>

              <Form.Item name="lang" noStyle>
                <Select
                    {...languageSelectProps}
                    options={[{ label: "All", value: "" }, ...(languageSelectProps.options ?? [])]}
                    style={{ width: 180 }}
                    onChange={() => searchFormProps.form?.submit()}
                    dropdownMatchSelectWidth={false}
                />
              </Form.Item>

              <Form.Item name="status" noStyle>
                <Select
                    options={[
                      { label: "All statuses", value: "" },
                      { label: t("quiz.status.enabled"), value: "enabled" },
                      { label: t("quiz.status.disabled"), value: "disabled" },
                      { label: t("quiz.status.for_review"), value: "for_review" },
                    ]}
                    style={{ width: 180 }}
                    onChange={() => searchFormProps.form?.submit()}
                    dropdownMatchSelectWidth={false}
                />
              </Form.Item>

              <Form.Item name="event_id" noStyle>
                <Select
                    {...eventSelectProps}
                    options={[{ label: "All", value: "" }, ...(eventSelectProps.options ?? [])]}
                    style={{ width: 180 }}
                    onChange={() => searchFormProps.form?.submit()}
                    dropdownMatchSelectWidth={false}
                />
              </Form.Item>

              <Form.Item name="keywords" noStyle>
                <KeywordTagsSelect
                    placeholder="Tags (type 3+ chars)"
                    style={{ width: 240 }}
                    onChange={() => searchFormProps.form?.submit()}
                    dropdownMatchSelectWidth={false}
                />
              </Form.Item>
            </Input.Group>
          </Form>,
          <DownloadButton apiUrl={apiUrl} />,
          <Upload
            accept=".csv"
            showUploadList={false}
            action={`${apiUrl}/quiz/bulk-upload`}
            headers={{Authorization: `Bearer ${APIToken}`}}
            onChange={handleChange}
          >
            <Button icon={loading ? <LoadingOutlined /> : <UploadOutlined />}>Bulk upload</Button>
          </Upload>,
          <CreateButton />,
          <DeleteAllButton apiUrl={apiUrl} APIToken={APIToken} />,
        ]
      }}
    >
      <Table {...tableProps} rowKey="quiz_id">
        <Table.Column
          dataIndex="quiz_id"
          key="quiz_id"
          title="ID"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("quiz_id", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="question"
          key="question"
          title={t("quiz.fields.question")}
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("question", sorter)}
        />
        <Table.Column
          dataIndex="quiz_category_name"
          key="quiz_category_name"
          title={t("quiz.fields.quiz_category_name")}
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("quiz_category_name", sorter)}
        />
        <Table.Column
          dataIndex="lang"
          key="lang"
          title={t("quiz.fields.lang")}
          render={(value) => <TextField value={value} />}
        />
        <Table.Column
            dataIndex="correct_perc"
            key="correct_perc"
            title={t("quiz.fields.correct_answers")}
            render={(_, record: { correct_perc: number; correct: number; count: number }) => {
              const { correct_perc, count, correct } = record;
              return <TextField value={`${correct_perc.toFixed(1)}% (${correct}/${count})`} />;
            }}
            defaultSortOrder={getDefaultSortOrder("correct_perc", sorter)}
            sorter
        />
        <Table.Column
            dataIndex="favourites_perc"
            key="favourites_perc"
            title={t("quiz.fields.favourited")}
            render={(_, record: { favourites_count: number; favourites_perc: number; count: number }) => {
              const { favourites_count, favourites_perc, count } = record;
              return <TextField value={`${favourites_perc.toFixed(1)}% (${favourites_count}/${count})`} />;
            }}
            defaultSortOrder={getDefaultSortOrder("favourites_perc", sorter)}
            sorter
        />
        <Table.Column
          dataIndex="status"
          key="status"
          title={t("quiz.fields.status")}
          render={(value) => <TextField value={value ? t(`quiz.status.${value}`) : ""} />}
          defaultSortOrder={getDefaultSortOrder("status", sorter)}
        />
        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title={t("quiz.fields.created_at")}
          render={(value) => <DateField value={value} format="LLL" />}
          defaultSortOrder={getDefaultSortOrder("created_at", sorter)}
        />
        <Table.Column<IQuiz>
          title={t("table.actions")}
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.quiz_id} />
              <ShowButton hideText size="small" recordItemId={record.quiz_id} />
              <DeleteButton
                hideText
                size="small"
                recordItemId={record.quiz_id}
                confirmTitle={
                  !record.parent_id
                    ? t("quiz.messages.delete_parent_warning")
                    : undefined
                }
              />
            </Space>
          )}
        />
      </Table>
    </List>)
  );
};
